"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import Image from "next/image"
import "swiper/css"
import { CustomImage, transitionDelayTime, TransitionUp } from "ui"
import { HistoryData, versionedAsset } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledContainer = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "$isReversed",
})<{ $isReversed?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  img {
    order: ${(props) => (props.$isReversed ? 1 : 0)};
    justify-self: ${(props) => (props.$isReversed ? "flex-start" : "flex-end")};
  }
`
const TextBox = styled.div<{ $isReversed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 180px;
  text-align: ${(props) => (props.$isReversed ? "right" : "left")};
  justify-self: ${(props) => (props.$isReversed ? "flex-end" : "flex-start")};
`

const TimelineLine = styled.div`
  position: absolute !important;
  width: 2px;
  height: 100%;
  left: 50%;
  transform: translateX(-50%);
  top: 0;
  background-image: url("/images/historyMobileLine.png");
  background-repeat: repeat-y;
  background-size: 2px 720px;
`

const StyledPin = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 200px;
  z-index: 1;
  box-sizing: content-box;
  border: 3px solid var(--White, #fff);
  background: var(--Brand-Light, #1bbd77);
  box-shadow: 0px 0px 16px 0px rgba(27, 189, 119, 0.8);
  position: absolute;
  left: 50%;
  transform: translateX(-50%); // Center the pin horizontally
`

const MobileBusWrapper = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "inView",
})<{ inView: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition:
    top 8s ease,
    opacity 0.1s ease 6s;
  top: ${({ inView }) => (inView ? "100%" : "0")};
  opacity: ${({ inView }) => (inView ? 0 : 1)};
  z-index: 2;
  width: 32px;
  height: 32px;
`

type Props = {
  data: HistoryData[]
  inView: boolean
  limited?: boolean
}

const HistoryTimeLine: React.FC<Props> = ({ data, inView, limited }) => {
  return (
    <StyledLayout>
      <TimelineLine />
      <MobileBusWrapper inView={inView}>
        <Image
          src={versionedAsset("/images/busHistoryMobile.png")}
          alt="History Timeline Bus"
          width={32}
          height={120}
        />
      </MobileBusWrapper>

      {data.slice(0, limited ? 4 : undefined).map((el, index) => (
        <TransitionUp
          $inView={inView}
          $delay={transitionDelayTime * 0.7 * index}
          key={index}
        >
          <StyledContainer $isReversed={index % 2 === 1} key={index}>
            <CustomImage
              src={getUmbracoMediaUrl(el.imageForMobil)}
              alt={el.title}
              width={136}
              height={136}
              style={{
                objectFit: "cover",
                borderRadius: 16,
              }}
            />
            <StyledPin />
            <TextBox $isReversed={index % 2 === 1}>
              <Typography variant="h2" lineHeight={"32px"}>
                {el.yearForHomepage
                  ? new Date(el.yearForHomepage).getFullYear()
                  : el.years}
              </Typography>
              <Typography variant="body1">{el.subtitle}</Typography>
            </TextBox>
          </StyledContainer>
        </TransitionUp>
      ))}
    </StyledLayout>
  )
}

export default HistoryTimeLine
